import type {
  BasicChallenge,
  ChallengeProvisioningDetails,
  ProvisioningCheck,
  ProvisioningCheckStatus,
  ProvisioningCheckType,
} from "../model/challenge.model";
import { type Planet, planets } from "../model/planet.model";

/**
 * Where a provisioning placeholder is in its flow:
 * - kyc: KYC not passed yet (also under review or rejected), the card goes to Profile > Verification
 * - contract: KYC passed, contract not signed yet (also not sent yet), the card goes to Contracts
 * - activating: both done, the challenge is being created, the card is not clickable
 * A denied contract never shows: the backend stops returning the placeholder then.
 */
export type ProvisioningStep = "kyc" | "contract" | "activating";

/** Route of the steps the trader can act on; activating has none. */
export const PROVISIONING_STEP_ROUTES: Partial<Record<ProvisioningStep, string>> = {
  kyc: "/profile/verification",
  contract: "/contracts",
};

function getCheckStatus(
  details: ChallengeProvisioningDetails,
  type: ProvisioningCheckType,
): ProvisioningCheckStatus {
  return (
    details.checks.find((check: ProvisioningCheck) => check.type === type)?.status ?? "NOT_STARTED"
  );
}

export function getProvisioningStep(details: ChallengeProvisioningDetails): ProvisioningStep {
  if (getCheckStatus(details, "KYC") !== "COMPLETED") return "kyc";
  if (getCheckStatus(details, "CONTRACT") !== "COMPLETED") return "contract";
  return "activating";
}

/** The first segment of the order UUID, e.g. "4be4d4a8" for "4be4d4a8-7399-...", "kyc_failed" for a stub id. */
export function getShortOrderId(orderId: string): string {
  return orderId.split("-")[0];
}

/** The card a placeholder is shown as, next to the real challenges. */
export function toProvisioningChallenge(details: ChallengeProvisioningDetails): BasicChallenge {
  const planet: Planet =
    planets.find((item: Planet) => item === details.planetName.toUpperCase()) ?? "SATURN";

  return {
    id: details.challengeId,
    userId: "",
    planetName: planet,
    accountNumber: getShortOrderId(details.challengeId),
    subscriptionType: details.subscriptionType,
    accountSize: details.accountSize,
    currentBalance: 0,
    accountCurrency: details.accountCurrency,
    startDate: "",
    accountState: "PROVISIONING",
    isDisplay: true,
    chargeSwapFees: details.chargeSwapFees,
    user: { login: "", password: "" },
    type: details.type,
    evaluationType: details.evaluationType,
    labels: [],
    provisioning: details,
  };
}
